import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: ${(props) => props.$zIndex - 1};
  opacity: ${(props) => (props.$open ? 1 : 0)};
  visibility: ${(props) => (props.$open ? 'visible' : 'hidden')};
  pointer-events: ${(props) => (props.$open ? 'auto' : 'none')};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
`;

const DrawerPanel = styled.div`
  position: fixed;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  transition: transform 0.3s ease;
  z-index: ${(props) => props.$zIndex};

  ${(props) => {
    const open = props.$open;
    const width = props.$width || '40%';
    const height = props.$height || '40%';
    switch (props.$placement) {
      case 'right':
        return `
          top: 0; right: 0; bottom: 0;
          width: ${width};
          height: 100vh;
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
          transform: translateX(${open ? '0' : '100%'});
        `;
      case 'left':
        return `
          top: 0; left: 0; bottom: 0;
          width: ${width};
          height: 100vh;
          box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
          transform: translateX(${open ? '0' : '-100%'});
        `;
      case 'bottom':
        return `
          left: 0; right: 0; bottom: 0;
          width: 100%;
          height: ${height};
          box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
          transform: translateY(${open ? '0' : '100%'});
        `;
      case 'top':
        return `
          left: 0; right: 0; top: 0;
          width: 100%;
          height: ${height};
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          transform: translateY(${open ? '0' : '-100%'});
        `;
      default:
        return `
          top: 0; right: 0; bottom: 0;
          width: ${width};
          height: 100vh;
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
          transform: translateX(${open ? '0' : '100%'});
        `;
    }
  }}
`;

const DrawerHeader = styled.div.attrs((props) => ({
  className: props.$headerClassName
}))`
  display: ${(props) => (props.$showHeader ? 'flex' : 'none')};
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  min-height: 56px;
  flex-shrink: 0;
`;

const DrawerTitle = styled.div.attrs((props) => ({
  className: props.$titleClassName
}))`
  font-size: 16px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
`;

const DrawerCloseButton = styled.button.attrs((props) => ({
  className: props.$closeButtonClassName
}))`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.45);
  transition: color 0.3s ease;

  &:hover {
    color: rgba(0, 0, 0, 0.85);
  }

  &::before {
    content: '×';
    font-size: 24px;
    line-height: 1;
  }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/**
 * Reusable Drawer - replaces antd Drawer.
 * Supports: right, left, top, bottom placement.
 */
const Drawer = ({
  open,
  onClose,
  placement = 'right',
  width = '40%',
  height = '40%',
  showHeader = false,
  title = '',
  closable = false,
  maskClosable = true,
  zIndex = 1030,
  bodyStyle = {},
  style = {},
  className = '',
  titleClassName = '',
  headerClassName = '',
  closeButtonClassName = '',
  bodyClassName = '',
  children
}) => {
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [open]);

  const handleOverlayClick = (e) => {
    if (maskClosable && e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const showHeaderContent = showHeader || (closable && title);

  const panelClassName = ['ant-drawer-content-wrapper', 'ant-drawer-content', className].filter(Boolean).join(' ');

  const drawerContent = (
    <>
      <DrawerOverlay $open={open} $zIndex={zIndex} onClick={handleOverlayClick} />
      <DrawerPanel
        $placement={placement}
        $open={open}
        $width={width}
        $height={height}
        $zIndex={zIndex}
        style={style}
        className={panelClassName}
      >
        <DrawerHeader $showHeader={showHeaderContent} $headerClassName={headerClassName}>
          <DrawerTitle $titleClassName={titleClassName}>{title}</DrawerTitle>
          {closable && <DrawerCloseButton $closeButtonClassName={closeButtonClassName} onClick={onClose} aria-label="Close" />}
        </DrawerHeader>
        <DrawerBody style={bodyStyle} className={`ant-drawer-body ${bodyClassName}`.trim()}>
          {children}
        </DrawerBody>
      </DrawerPanel>
    </>
  );

  return ReactDOM.createPortal(drawerContent, document.body);
};

export { Drawer };
export default Drawer;
